<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<template>
  <div>
    <div v-if="!pageLoading && !sidebarLoading">
      <div class="tabs mb-4">
        <div
          v-for="({
            label,
            value,
            description,
            tooltip,
            disabled,
            exclusionKey,
            exclusionDescription,
          }) in tabsWithTooltips"
          :key="label"
          class="tab"
        >
          <div class="d-flex align-center justify-space-between mb-2">
            <h5 class="label">
              {{ label }}
            </h5>
            <InsightsTooltip
              :tooltip="tooltip"
            />
          </div>
          <div
            v-if="!exclusions[exclusionKey]"
            class="mb-2 percentage"
            :class="{ disabled: disabled || !documents.length }"
          >
            {{ !documents.length ? '0' : value || '0' }}%
          </div>
          <div class="text-body-3 text-textLight">
            {{ exclusions[exclusionKey] ? exclusionDescription : description }}
          </div>
        </div>
      </div>
      <div class="content">
        <TableContent
          :table-label="tableLabel"
          :documents="documents"
          :statements="statements"
          :selected-jobs="selectedReportJobs"
          :disable-share="disableShare"
          :document-type="documentType"
          :is-shared-page="isSharedPage"
          @update-selected="setSelectedReportJobs"
          @select-all="handleSelectAll"
          @share="handleShareButtonClick"
        >
          <template #noResultsFoundText>
            <slot name="noResultsFoundText" />
          </template>
        </TableContent>
      </div>
      <div
        v-if="documents.length"
        class="legend-slot-container mt-2"
      >
        <slot name="legend" />
      </div>
      <ActionableInsightShareModal
        :selected-jobs="selectedReportJobs"
        @close="setModal({
          field: 'actionableInsight',
          value: false,
        })"
      />
    </div>
    <ActionableInsightSkeletonLoader
      v-else
      :table-title="labelsForLoading?.tableLabel"
    />
  </div>
</template>

<script>
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { mapState, mapActions } from 'pinia';
import { useModals } from '@/stores/modals/modals';
import { useAppLoading } from '@/stores/app/loading';
import { useActionableInsights } from '@/stores/insights/actionableInsights';
import { useInsights } from '@/stores/insights/insights';
import ActionableInsightShareModal from '@/components/insights/ActionableInsight/ActionableInsightShareModal.vue';
import ActionableInsightSkeletonLoader from '@/components/insights/ActionableInsight/ActionableInsightSkeletonLoader.vue';
import TableContent from '@/components/insights/ActionableInsight/TableContent.vue';
import { useTooltips } from '@/stores/insights/tooltips';

export default {
  name: 'ActionableInsight',
  components: {
    InsightsTooltip,
    TableContent,
    ActionableInsightShareModal,
    ActionableInsightSkeletonLoader,
  },
  props: {
    tabs: {
      type: Array,
      default() {
        return [];
      },
    },
    documents: {
      type: Array,
      default() {
        return [];
      },
    },
    statements: {
      type: Array,
      default() {
        return [];
      },
    },
    tableLabel: {
      type: String,
      default: '',
    },
    disableShare: {
      type: Boolean,
      default: false,
    },
    documentType: {
      type: String,
      default: 'job',
    },
    isSharedPage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      coverPosition: ['0px', '-10%', '-20%'],
    };
  },
  computed: {
    ...mapState(useActionableInsights, ['selectedReportJobs', 'exclusions']),
    ...mapState(useAppLoading, ['pageLoading', 'sidebarLoading']),
    ...mapState(useTooltips, ['tooltips']),
    ...mapState(useInsights, ['labelsForLoading']),
    tabsWithTooltips() {
      return this.tabs.map((tab) => {
        const { body_r: html, mode } = this.tooltips?.[tab.tooltipKey] || {};

        return {
          ...tab,
          tooltip: { html, mode, key: tab.tooltipKey },
        };
      });
    },
    tabsLength() {
      return this.tabs.length;
    },
  },
  beforeUnmount() {
    // Reset selected jobs
    this.setSelectedReportJobs([]);
  },
  methods: {
    ...mapActions(useActionableInsights, ['setSelectedReportJobs']),
    ...mapActions(useModals, ['setModal']),
    handleShareButtonClick() {
      this.setModal({ field: 'actionableInsight', value: true });
    },
    handleSelectAll(selectedJobs) {
      let newSelectedJobs = this.selectedReportJobs;
      /* Select or deselect all should preserve the selected
      jobs that are not on the current pagination page */
      const selectAllIds = this.documents.map(({ id }) => id);
      const selectedJobsNotOnPage = newSelectedJobs
        .filter(({ id }) => !selectAllIds.includes(id));

      if (selectedJobs?.length) {
        newSelectedJobs = [...selectedJobsNotOnPage, ...selectedJobs];
      } else {
        newSelectedJobs = selectedJobsNotOnPage;
      }

      this.setSelectedReportJobs(newSelectedJobs);
    },
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.tabs {
  display: grid;
  grid-template-columns: repeat(v-bind(tabsLength), 1fr);
  gap: 16px;
}

.tab {
  flex: 1;
  background: @white;
  min-height: 145px;
  max-height: 240px;
  border-radius: 8px;
  padding: 24px;
  z-index: 2;
  position: relative;
  border: 1px solid @stroke-default;
}

.content {
  background: @white;
  min-height: 476px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid @stroke-default;
  padding: 0px;
}

.legend-slot-container {
  background: @white;
  width: 100%;
  border-radius: 8px;
  border: 1px solid @stroke-default;
  padding: 24px;
}

.percentage {
  color: @text-darkest;
  font-size: 36px;
  font-weight: 700;
  line-height: 48px;

  &.disabled {
    color: @grey-500;
  }
}
</style>
